import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const fetchCart = useCallback(async (cartId) => {
    try {
      const response = await axios.get(`${API}/cart/${cartId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      localStorage.removeItem('cartId');
    }
  }, []);

  useEffect(() => {
    const cartId = localStorage.getItem('cartId');
    if (cartId) {
      fetchCart(cartId);
    }
  }, [fetchCart]);

  const addToCart = useCallback(async (productId, size, quantity = 1) => {
    setLoading(true);
    try {
      const cartId = localStorage.getItem('cartId');
      const response = await axios.post(`${API}/cart/add`, {
        cart_id: cartId || null,
        product_id: productId,
        size,
        quantity
      });
      
      setCart(response.data);
      localStorage.setItem('cartId', response.data.id);
      setIsCartOpen(true);
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCartItem = useCallback(async (productId, size, quantity) => {
    setCart(prev => {
      if (!prev) return prev;
      return prev;
    });
    setLoading(true);
    try {
      const currentCart = cart;
      if (!currentCart) return;
      const response = await axios.put(
        `${API}/cart/${currentCart.id}/item/${productId}/${encodeURIComponent(size)}`,
        { quantity }
      );
      setCart(response.data);
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [cart]);

  const removeFromCart = useCallback(async (productId, size) => {
    setLoading(true);
    try {
      const currentCart = cart;
      if (!currentCart) return;
      const response = await axios.delete(
        `${API}/cart/${currentCart.id}/item/${productId}/${encodeURIComponent(size)}`
      );
      setCart(response.data);
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [cart]);

  const clearCart = useCallback(() => {
    setCart(null);
    localStorage.removeItem('cartId');
  }, []);

  const getCartTotal = useCallback(() => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cart]);

  const getCartCount = useCallback(() => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    loading,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartCount
  }), [cart, loading, isCartOpen, addToCart, updateCartItem, removeFromCart, clearCart, getCartTotal, getCartCount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
